import type { ClientOrganizationProfileRoleEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevineOrganizationApi } from '@/features/api/hooks/grapevine-organization/useGrapevineOrganizationApi';

type Variables = {
  organizationId: string;
  roleAssignmentId: string;
  roleKey: ClientOrganizationProfileRoleEnum;
};

// Note: the response's roleAssignmentId differs from the request's — the backend
// deletes + recreates atomically. Always update local state with the returned id.
export const useChangeOrganizationMemberRole = () => {
  const api = useGrapevineOrganizationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, roleAssignmentId, roleKey }: Variables) =>
      api.changeOrganizationMemberRole({
        organizationId,
        roleAssignmentId,
        clientOrganizationMemberRoleChangeRequest: { roleKey }
      }),
    onSuccess: (_data, { organizationId }) => {
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.ORGANIZATION_MEMBERS, organizationId]
      });
      // The affected person's own-organization list reflects the new role.
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.OWN_ORGANIZATIONS]
      });
      // The organization detail embeds its members.
      queryClient.invalidateQueries({
        queryKey: [API_CACHE_KEYS.ORGANIZATION, organizationId]
      });
    }
  });
};
